import { Injectable } from '@angular/core';
import { Park } from './class/park';
import { Portfolio } from './class/portfolio';
import { Deferred } from '../deferred';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, of } from 'rxjs';
import { shareReplay, map } from 'rxjs/operators';
import { Turbine } from './class/turbine';
import { Period } from './class/period';
import { DimensionDataService } from 'app/shared/services-api-v2/dimension-data/dimension-data.service';
import { DimensionPortfolioDataService } from 'app/shared/services/dimension-portfolio-data/dimension-portfolio-data.service';
import { DeviceGroupsService } from 'app/shared/services-api-v2/device-groups/device-groups.service';

@Injectable({ providedIn: 'root' })
export class DimensionService {
  parkList: Array<Park>;
  parkListDef: Deferred<Array<Park>>;
  parkList$: ReplaySubject<Array<Park>>;
  portfolioList: Array<Portfolio>;
  portfolioListDef: Deferred<Array<Portfolio>>;
  portfolioList$: ReplaySubject<Array<Portfolio>>;
  turbineList: Array<Turbine>;
  turbineListDef: Deferred<Array<Turbine>>;
  turbineList$: ReplaySubject<Array<Turbine>>;
  periodList: Array<Period>;
  periodListDef: Deferred<Array<Period>>;
  periodListDefMonitor: Deferred<Array<Period>>;
  periodList$: ReplaySubject<Array<Period>>;
  private assetDataLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  parkMap;
  parkMapName;

  constructor(
    private dimensionDataService: DimensionDataService,
    private dimensionPortfolioDataService: DimensionPortfolioDataService,
    private deviceGroupsService: DeviceGroupsService,
  ) {
    this.parkListDef = new Deferred();
    this.parkList$ = new ReplaySubject();
    this.parkList = new Array();
    this.portfolioListDef = new Deferred();
    this.portfolioList$ = new ReplaySubject();
    this.portfolioList = new Array();
    this.turbineList = new Array();
    this.turbineListDef = new Deferred();
    this.turbineList$ = new ReplaySubject();
    this.periodList = new Array();
    this.periodListDef = new Deferred();
    this.periodListDefMonitor = new Deferred();
    this.periodList$ = new ReplaySubject();

    this.parkMap = {};
    this.parkMapName = {};
    combineLatest([
      this.dimensionDataService.getParksMetadata(),
      this.dimensionPortfolioDataService.getUserPortfolios(),
      this.deviceGroupsService.getGroupsData(),
    ]).subscribe(([parks, portfolios, deviceGroups]) => {
      const allParks = portfolios.flatMap(p => p.parks)
      portfolios.forEach(portfolio => {
        const parks = portfolio.parks.map(park => park._id);
        this.portfolioList.push(new Portfolio(portfolio._id, portfolio.name, portfolio.defaultPortfolio, parks));
        this.portfolioListDef.resolve(this.portfolioList);
        this.portfolioList$.next(this.portfolioList);
      });

      parks.forEach(e => {
        this.parkMap[e.code] = this.parkList.length;
        this.parkMapName[e.name] = this.parkList.length;
        const energyMeterAvailable = allParks.find(p => p._id === e._id).energyMeterAvailable;
        this.parkList.push(
          new Park(
            e._id,
            e.code,
            e.displayName,
            e.name,
            e.COUNTRY.name,
            e.COUNTRY.code,
            e.latitude,
            e.longitude,
            e.timezone,
            deviceGroups.find(dg => dg._id_park === e._id)?.turbine_groups ?? [],
            energyMeterAvailable,
            e.useRevenuesV2InBudgets,
            e.displayCode || e.code,
          ),
        );
        this.parkListDef.resolve(this.parkList);
        this.parkList$.next(this.parkList);
        e.TURBINES.forEach(el => {
          this.turbineList.push(
            new Turbine(
              el._id,
              el.serialNumeric,
              e._id,
              el.code,
              e.name,
              el.ratedPower,
              el.latitude,
              el.longitude,
              el.serial,
              el.codeDisplay,
              el.type,
              el.serialDisplay,
            ),
          );
        });
        this.turbineListDef.resolve(this.turbineList);
        this.turbineList$.next(this.turbineList);
      });
      this.assetDataLoaded$.next(true);
    });
  }

  public updateTurbineGroups() {
    this.parkList?.forEach(park => {
      const deviceGroup = this.deviceGroupsService.getGroupsDataSync().find(dg => dg._id_park === park.id);
      if (deviceGroup) {
        park.groups = deviceGroup.turbine_groups;
      }
    });
  }

  public isMAssetDataLoaded(): Observable<boolean> {
    return this.assetDataLoaded$.asObservable();
  }

  getPortfolioList(): Promise<Array<Portfolio>> {
    return this.portfolioListDef.promise;
  }

  getParkList(): Promise<Array<Park>> {
    return this.parkListDef.promise.then(parks => {
      const parkList = [...parks];
      const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
      parkList.sort((a, b) => collator.compare(a.frontEndCode, b.frontEndCode));
      return parkList;
    });
  }

  getTurbineList(): Promise<Array<any>> {
    return this.turbineListDef.promise.then(turbines => {
      const turbineList = [...turbines];
      const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
      turbineList.sort((a, b) => collator.compare(a.name, b.name));
      return turbineList;
    });
  }

  getTurbineBySerial(serial) {
    return this.turbineListDef.promise.then(tl => {
      return tl.find(t => t.id_device === serial);
    });
  }

  getTurbineById(id) {
    return this.turbineListDef.promise.then(tl => {
      return tl.find(t => t._id === id);
    });
  }


  getTurbineByName(name: any) {
    return this.turbineListDef.promise.then(tb => {
      return tb.find(e => {
        return e.id_device === name;
      });
    });
  }

  getParkByName(name: string): Promise<Park> {
    return this.getParkList().then(pl => {
      return this.parkList[this.parkMapName[name]];
    });
  }

  getParkByCode(code): Promise<Park> {
    return this.getParkList().then(pl => {
      return pl.find(x => x.code === code);
    });
  }

  getParkById(_id): Promise<Park> {
    return this.getParkList().then(pl => {
      return pl.find(x => x.id === _id);
    });
  }
  getPeriodList(): Promise<Array<Period>> {
    return this.periodListDef.promise;
  }

  getParkTurbinesByName(parkName): Observable<Turbine[]> {
    return this.turbineList$.pipe(map((turbines: Turbine[]) => turbines.filter(e => e.parkName === parkName)));
  }

  getParkTurbinesByCode(parkCode): Promise<Array<any>> {
    return new Promise(resolve =>
      this.getTurbineList().then(turbines =>
        this.getParkList().then(pl => {
          const parkTurbines = [];
          pl.map(pk =>
            pk.code === parkCode
              ? turbines.map(turbine => (pk.name === turbine.parkName ? parkTurbines.push(turbine) : null))
              : null,
          );
          const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
          parkTurbines.sort((a, b) => collator.compare(a.name, b.name));
          resolve(parkTurbines);
        }),
      ),
    );
  }


  getCountryList(): Promise<Array<any>> {
    return this.dimensionDataService.getCountries().toPromise();
  }

  getParkTimezoneByNameOrId(parkNameOrId: string): string {
    if (!parkNameOrId) {
      return this.parkList[0]?.timezone;
    } else {
      return this.parkList.find(p => p.name === parkNameOrId || p.id === parkNameOrId)?.timezone;
    }
  }

  getParkTimezoneByTurbineSerial(serial: any): string {
    const turbine = this.turbineList.find(t => t.id_device === Number(serial) || t.serial == serial);
    return this.parkList.find(p => p.id === turbine.id_windpark)?.timezone;
  }

  getTurbineCodeDisplay(identifier: string): string {
    const turbine = this.turbineList.find(
      t => t.code === identifier || t.serial === identifier || t._id === identifier || t.id_device === +identifier,
    );
    return turbine?.codeDisplay || identifier;
  }

  getTurbineByIdentifier(identifier: string): Turbine {
    return this.turbineList.find(
      t => t.code === identifier || t.serial === identifier || t._id === identifier || t.id_device === +identifier,
    );
  }

  getPortfolioListSync(): Array<Portfolio> {
    return this.portfolioList;
  }

  getParkListSync(): Array<Park> {
    return this.parkList;
  }
}
